import * as React from "react";

import { Component as List } from "@components/list/index";
import * as ListItem from "@components/list-item/index";
import { Skeleton } from "@components/skeleton/index";
import { DaperStory } from "@data/daper-client/types";
import {
  PersonalizationRelatedParams,
  PersonalizationStory,
} from "@data/personalization-client/types";
import { IntercomTracking } from "@interface/IntercomSlot";
import type {
  IntercomComponentProps,
  IntercomInterface,
} from "@interface/types";
import { truthyFilter } from "@util/truthyFilter";
import { type AdRenderer } from "types/types";

import styles from "./styles.module.css";

export type Props = {
  intercom: IntercomInterface;
  personalizationRelatedParams: PersonalizationRelatedParams;
  numberOfRows?: number;
  adRenderers?: AdRenderer[];
  title?: string;
} & IntercomComponentProps;

export const getRelatedStoriesWithImagesForComponent = async (props: {
  intercom: IntercomInterface;
  personalizationRelatedParams: PersonalizationRelatedParams;
}) => {
  const { intercom, personalizationRelatedParams } = props;

  const relatedStories = (await intercom.getRelated(
    personalizationRelatedParams
  )) as PersonalizationStory[];

  const responses = await Promise.allSettled(
    relatedStories.map(async (relatedStory) => {
      const storySearchResponse = (await intercom.getStories({
        id: relatedStory.assetID,
        "attachments.image": true,
      })) as DaperStory[];

      if (!storySearchResponse.length) {
        throw Error(
          `No stories returned from getStories with id=${relatedStory.assetID}`
        );
      }

      const story = storySearchResponse[0];

      if (!relatedStory.thumbnailUrl) {
        const ledeImageId = story.extraMedia?.lede?.default.id;

        if (!ledeImageId) return null;

        const images = await intercom.getImages({ id: ledeImageId });

        return { ...story, images };
      }

      return { ...story, images: { baseUrl: relatedStory.thumbnailUrl } };
    })
  );

  return responses
    .map((response) =>
      response && response.status === "fulfilled" ? response.value : null
    )
    .filter(truthyFilter);
};

export function RelatedStoriesListItemList(props: Props) {
  const {
    intercom,
    personalizationRelatedParams,
    numberOfRows = 5,
    adRenderers,
    title,
  } = props;

  const [componentPropsList, setComponentPropsList] = React.useState<
    ListItem.Props[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(true);
    getRelatedStoriesWithImagesForComponent({
      intercom,
      personalizationRelatedParams: {
        ...personalizationRelatedParams,
        limit: numberOfRows,
      },
    }).then((stories) => {
      const propsList = stories.map((story) => ({
        image: {
          assetUrl: story.images.baseUrl,
          isLazy: true,
          aspectRatio: 3 / 2,
          mobileMaxWidth: 103,
          desktopMaxWidth: 141,
          linkToUrl: story.url,
        },
        text: story.headlines.web,
        url: story.url,
      }));

      setComponentPropsList(propsList);
      setIsLoading(false);
    });
  }, [numberOfRows, personalizationRelatedParams]);

  const ListItemWithSkeleton = (componentProps: ListItem.Props) => (
    <Skeleton isLoading={isLoading}>
      <ListItem.WithImageOnLeft {...componentProps} />
    </Skeleton>
  );

  return (
    <IntercomTracking componentName="RelatedStoriesListItemList">
      {title && <h3 className={styles.title}>{title}</h3>}
      <List
        IterableComponent={ListItemWithSkeleton}
        componentPropsList={componentPropsList}
        numberOfRows={numberOfRows}
        adRenderers={adRenderers}
      />
    </IntercomTracking>
  );
}
